import NonControlledHitterStackMarker from 'components/organisms/non-controlled-hitter-stacking/services/non-controlled-hitter-stack-marker';
import {getPlayersGroupUID, getSimplexUnitName} from 'helpers/simplex-helper';
import Const from 'constants';
import {countEntries, getRandomArray} from 'helpers/array-helper';
import {calcAndRoundDownValByPct, calcAndRoundUpValByPct} from 'helpers/common-helper';
import RuleBuilder from 'services/simplex/rule-builder';

function createConstraintsForExcludeTeamsCombinations(params) {
    const {lineupTeamsCombinations, rawLineups, lineupsQuantity, constraints} = params;
    const items = lineupTeamsCombinations.exposureMax.all();

    let result = [];

    if (isEmpty(constraints)) {
        return result;
    }

    const firstGroup = constraints[Const.MLB_NON_CONTROLLED_FIRST_GROUP];
    const secondGroup = constraints[Const.MLB_NON_CONTROLLED_SECOND_GROUP];

    Object.values(items).map((item) => {
        let usageQuantity = 0;
        rawLineups.asArr().map((lineup) => {
            const diff = arrayDiff(item.teamsCombination, lineup.getTeamNames());
            if (isEmpty(diff)) {
                usageQuantity++;
            }
        });

        const expMax = item.value;
        const usagePercent = ((usageQuantity + 1) / lineupsQuantity.getLineupsQuantity()) * 100;

        if (usagePercent > expMax) {
            const [firstTeam, secondTeam] = item.teamsCombination;

            if (firstGroup.hasOwnProperty(firstTeam) && secondGroup.hasOwnProperty(secondTeam)) {
                result.push({
                    row: {
                        [firstGroup[firstTeam].groupName]: 1,
                        [secondGroup[secondTeam].groupName]: 1,
                    },
                    type: Const.STACKS_NUMBER_TYPE_AT_MOST,
                    obj: 1,
                });
            }

            if (secondGroup.hasOwnProperty(firstTeam) && firstGroup.hasOwnProperty(secondTeam)) {
                result.push({
                    row: {
                        [secondGroup[firstTeam].groupName]: 1,
                        [firstGroup[secondTeam].groupName]: 1,
                    },
                    type: Const.STACKS_NUMBER_TYPE_AT_MOST,
                    obj: 1,
                });
            }
        }
    });

    return [...result];
}

function createPreparedGroupsConstraints(params) {
    const {constraints, doubleTeamsConstraints} = params;

    let result = [],
        usedGroups = [];

    Object.values(constraints).map((group) => {
        if (isEmpty(group)) {
            return;
        }
        let row = {};
        Object.values(group).map((item) => {
            row[item.groupName] = 1;
            usedGroups.push(item.groupName);
        });
        if (!isEmpty(row)) {
            result.push({
                row: {...row},
                type: Const.STACKS_NUMBER_TYPE_EXACTLY,
                obj: 1,
            });
        }
    });

    // One double teams constraint can have only 2 groups.
    // If just 2 groups are used and double teams constraint has 1 item,
    // then groups constraint needs to be recreated.
    if (1 === doubleTeamsConstraints.length && 2 === usedGroups.length) {
        let row = {};
        usedGroups.map((item) => (row[item] = 1));
        result = [
            {
                row: {...row},
                type: Const.STACKS_NUMBER_TYPE_EXACTLY,
                obj: 1,
            },
        ];
    }

    return [...result];
}

function createConstraintsForIncludedTeamsByExposureMin(params) {
    const {constraints, lineupsQuantity, usedGroups, stacking} = params;

    let result = [];

    Object.keys(constraints).map((groupNumber) => {
        const group = constraints[groupNumber];
        if (isEmpty(group)) {
            return;
        }
        let row = {};
        Object.values(group).map((item) => {
            const expMin = stacking.getTeamMinExposureInStackNumber(item.teamName, groupNumber);

            const reservedQuantity = calcAndRoundDownValByPct(lineupsQuantity.getLineupsQuantity(), expMin);
            const usageQuantity = countEntries(item.groupName, usedGroups);
            // console.log(item.groupName, reservedQuantity, usageQuantity);
            if (0 < reservedQuantity - usageQuantity) {
                row[item.groupName] = 1;
            }
        });
        // console.log('row',{...row});
        if (!isEmpty(row)) {
            result.push({
                row: {...row},
                type: Const.STACKS_NUMBER_TYPE_AT_LEAST,
                obj: 1,
            });
        }
    });

    return [...result];
}

function createObjectiveForRandomlyChosenPlayers(params) {
    const {mappedPlayersByTeams, constraints, projectionPointsPropName, getUnitIdsByPlayer} = params;

    function getAvailableTeamNames(constraints) {
        let availableTeamNames = [];
        Object.values(constraints).map((groupConstraints) => {
            availableTeamNames = [...availableTeamNames, ...Object.keys(groupConstraints)];
        });
        return [...new Set(availableTeamNames)];
    }

    const availableTeamNames = getAvailableTeamNames(constraints);
    const randomTeams = getRandomArray(availableTeamNames, 2);

    const objective = {};
    randomTeams.map((teamName) => {
        const availablePlayers = mappedPlayersByTeams[teamName];
        const randomPlayers = getRandomArray(availablePlayers, 3);

        randomPlayers.map((player) => {
            const pointsValue = player[projectionPointsPropName];
            const multiplyValue = Const.NON_CONTROLLED_HITTER_STACKING_PROJECTION_MULTIPLY_VALUE;

            const projectionPoints = pointsValue * multiplyValue;

            getUnitIdsByPlayer(player).map((playerId) => {
                objective[getSimplexUnitName(playerId)] = parseInt(projectionPoints);
            });
        });
    });

    return {...objective};
}

function createPreparedTeamsConstraints(params) {
    const {stacking, mlbFilters, mappedPlayersByTeams, getUnitIdsByPlayer} = params;
    const stacks = stacking.getStacks();
    const combinations = stacking.getCheckedCombinations();

    const constraints = {};
    Object.keys(stacks).map((stackNumber) => {
        const {order} = stacks[stackNumber];
        const orderPositions = getAvailableOrderPositions(order);

        if (isEmpty(orderPositions)) {
            return;
        }

        constraints[stackNumber] = createConstraintsForTeams({
            teams: stacking.getActiveTeamsByStackNumber(stackNumber),
            orderPositions,
            stackNumber,
            getUnitIdsByPlayer,
        });
    });

    const comboConstraints = {};
    Object.keys(combinations).map((comboKey) => {
        const combo = combinations[comboKey];

        comboConstraints[comboKey] = {};

        Object.keys(constraints).map((stackNumber) => {
            comboConstraints[comboKey][stackNumber] = {};

            Object.keys(constraints[stackNumber]).map((teamName) => {
                const constraint = constraints[stackNumber][teamName];
                const groupName = getGroupName(teamName, stackNumber);
                const row = cp(constraint.row);

                row[groupName] = -1 * combo.playersQuantity[stackNumber];
                comboConstraints[comboKey][stackNumber][teamName] = {
                    teamName,
                    groupName,
                    constraint: {...constraint, row},
                };
            });
        });
    });

    function createConstraintsForTeams(params) {
        const {teams, orderPositions, stackNumber, getUnitIdsByPlayer} = params;
        const constraints = {};

        Object.keys(teams).map((teamName) => {
            const isTeamChecked = stacking.isTeamCheckedInStackNumber(teamName, stackNumber);

            if (!isTeamChecked || !mappedPlayersByTeams.hasOwnProperty(teamName)) {
                return;
            }

            const row = {};
            mappedPlayersByTeams[teamName].map((player) => {
                if (!player.isPitcher() && inArray(Number(player.order), orderPositions)) {
                    getUnitIdsByPlayer(player).map((playerId) => (row[getSimplexUnitName(playerId)] = 1));
                }
            });

            if (!isEmpty(row)) {
                constraints[teamName] = {
                    row,
                    type: Const.STACKS_NUMBER_TYPE_AT_LEAST,
                    obj: 0,
                };
            }
        });

        return {...constraints};
    }

    function getAvailableOrderPositions(order) {
        const orderPositions = [];
        Object.keys(order).map((orderPosition) => {
            if (order[orderPosition] && mlbFilters.order[orderPosition]) {
                orderPositions.push(Number(orderPosition));
            }
        });
        return orderPositions;
    }

    function getGroupName(teamName, stackNumber) {
        const groupName = getPlayersGroupUID(teamName, stackNumber);
        return NonControlledHitterStackMarker.setSuffix(groupName);
    }

    return {...comboConstraints};
}

function createPreparedDoubleTeamsConstraints(params) {
    const {constraints} = params;

    let result = [];
    if (
        !constraints.hasOwnProperty(Const.MLB_NON_CONTROLLED_FIRST_GROUP) ||
        !constraints.hasOwnProperty(Const.MLB_NON_CONTROLLED_SECOND_GROUP)
    ) {
        return result;
    }

    Object.values(constraints[Const.MLB_NON_CONTROLLED_FIRST_GROUP]).map((teamG1) => {
        for (let teamG2 of Object.values(constraints[Const.MLB_NON_CONTROLLED_SECOND_GROUP])) {
            if (teamG1.teamName === teamG2.teamName) {
                result.push({
                    row: {
                        [teamG1.groupName]: 1,
                        [teamG2.groupName]: 1,
                    },
                    type: Const.STACKS_NUMBER_TYPE_AT_MOST,
                    obj: 1,
                });
                break;
            }
        }
    });

    return [...result];
}

function createConstraintsForExcludedTeamsByExposureMax(params) {
    const {teamsConstraints, lineupsQuantity, usedGroups, stacking} = params;

    let constraints = cp(teamsConstraints);

    Object.keys(teamsConstraints).map((groupNumber) => {
        const group = teamsConstraints[groupNumber];

        Object.keys(group).map((key) => {
            const team = group[key];
            const expMax = stacking.getTeamMaxExposureInStackNumber(team.teamName, groupNumber);
            const usageQuantity = countEntries(team.groupName, usedGroups);
            const usagePercent = ((usageQuantity + 1) / lineupsQuantity.getLineupsQuantity()) * 100;

            if (usagePercent > expMax) {
                delete constraints[groupNumber][key];
            }
        });
    });

    return {...constraints};
}

function createRestrictedPlayersConstraints(params) {
    const {constraints, restrictedPlayers, players, getUnitIdsByPlayer} = params;

    const playerIds = restrictedPlayers.getRestrictedPlayerIds();

    if (isEmpty(playerIds)) {
        return [];
    }

    const playerGroupsMap = {};

    Object.values(constraints).map((group) => {
        Object.values(group).map((team) => {
            Object.keys(team.constraint.row).map((unitName) => {
                if (!playerGroupsMap.hasOwnProperty(unitName)) {
                    playerGroupsMap[unitName] = [];
                }
                playerGroupsMap[unitName].push(team.groupName);
            });
        });
    });

    const shouldPlayerBeSkipped = (playerId) => !(playerId in players) || players[playerId].isPitcher();

    const result = [];
    playerIds.map((playerId) => {
        if (shouldPlayerBeSkipped(playerId)) {
            return;
        }

        getUnitIdsByPlayer(players[playerId]).map((playerId) => {
            const unitName = getSimplexUnitName(playerId);
            const ruleBuilder = new RuleBuilder();

            if (playerGroupsMap.hasOwnProperty(unitName)) {
                playerGroupsMap[unitName].map((groupName) => {
                    ruleBuilder.addVar(groupName, 1);
                });
                ruleBuilder.addVar(unitName, -1);
                ruleBuilder.atLeast(0);
            } else {
                ruleBuilder.addVar(unitName, 1);
                ruleBuilder.exactly(0);
            }

            result.push(ruleBuilder.createRule());
        });
    });

    return result;
}

function createDisabledPlayersConstraints(params) {
    const {restrictedPlayers, players, getUnitIdsByPlayer} = params;

    const playerIds = restrictedPlayers.getRestrictedPlayerIds();

    if (isEmpty(playerIds)) {
        return [];
    }

    const shouldPlayerBeSkipped = (playerId) => !(playerId in players) || players[playerId].isPitcher();

    const ruleBuilder = RuleBuilder.fromExactly(0);

    playerIds.map((playerId) => {
        if (shouldPlayerBeSkipped(playerId)) {
            return;
        }

        getUnitIdsByPlayer(players[playerId]).map((playerId) => {
            ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
        });
    });

    if (!ruleBuilder.isRuleCreatable()) {
        return [];
    }

    const constraint = ruleBuilder.createRule();

    return [constraint];
}

function buildCombinationsQuantity(params) {
    const {stacking, lineupsQuantity} = params;

    const combinations = stacking.getCheckedCombinations();

    const combinationsQuantity = [];
    Object.keys(combinations).map((comboKey) => {
        const combo = combinations[comboKey];

        combinationsQuantity.push({
            comboKey,
            lineupsQuantity: calcAndRoundUpValByPct(lineupsQuantity.getLineupsQuantity(), combo.exposure),
        });
    });

    return [...combinationsQuantity];
}

class ConstraintsBuilder {
    constructor(dependencies) {
        this._dependencies = {...dependencies};
    }

    createObjectiveForRandomlyChosenPlayers(params) {
        const {mappedPlayersByTeams, getUnitIdsByPlayer} = this._dependencies;
        const {constraints, projectionPointsPropName} = params;
        return createObjectiveForRandomlyChosenPlayers({
            constraints,
            mappedPlayersByTeams,
            projectionPointsPropName,
            getUnitIdsByPlayer,
        });
    }

    createConstraintsForExcludeTeamsCombinations(params) {
        return createConstraintsForExcludeTeamsCombinations(params);
    }

    createPreparedGroupsConstraints(params) {
        return createPreparedGroupsConstraints(params);
    }

    createConstraintsForIncludedTeamsByExposureMin(params) {
        const {stacking} = this._dependencies;
        return createConstraintsForIncludedTeamsByExposureMin({
            stacking,
            ...params,
        });
    }

    createPreparedDoubleTeamsConstraints(params) {
        return createPreparedDoubleTeamsConstraints(params);
    }

    createConstraintsForExcludedTeamsByExposureMax(params) {
        const {stacking} = this._dependencies;
        return createConstraintsForExcludedTeamsByExposureMax({
            stacking,
            ...params,
        });
    }

    createRestrictedPlayersConstraints(params) {
        const {stacking, getUnitIdsByPlayer} = this._dependencies;
        return createRestrictedPlayersConstraints({
            stacking,
            getUnitIdsByPlayer,
            ...params,
        });
    }

    createDisabledPlayersConstraints(params) {
        const {stacking, getUnitIdsByPlayer} = this._dependencies;
        return createDisabledPlayersConstraints({
            stacking,
            getUnitIdsByPlayer,
            ...params,
        });
    }

    createPreparedTeamsConstraints(params) {
        const {stacking, mappedPlayersByTeams, getUnitIdsByPlayer} = this._dependencies;
        return createPreparedTeamsConstraints({
            stacking,
            mappedPlayersByTeams,
            getUnitIdsByPlayer,
            ...params,
        });
    }

    buildCombinationsQuantity(params) {
        const {stacking} = this._dependencies;
        return buildCombinationsQuantity({stacking, ...params});
    }
}

export {ConstraintsBuilder};
